import { Avatar, Box, Button, Text } from "@mantine/core"
import { ArrowsClockwiseIcon, BuildingsIcon, TagIcon } from "@phosphor-icons/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { HelpIcon } from "@dehub/theme"
import { useNavigate } from "react-router"
import useSWR from "swr"
import { InvoiceApi, NetworkApi, OrderApi, PricingApi } from "../../../apis"
import { discountValueText, termOptions } from "../../../components/buyer-sales/product-list/cart-model"
import { useBuyerSuppliers } from "../../../components/buyer-sales/quick-order/use-buyer-supplier"
import { orderSupplierId, useReorder } from "../../../components/buyer-sales/reorder"
import { DotPill, type Tone } from "../../../components/delivery/atoms"
import { usePermission } from "../../../components/permission"
import { InfoRow } from "../../../components/ui"
import { currencyFormat } from "../../../utils"
import { dateFormat } from "../../../utils/date"
import { orderStatusUi } from "../../../utils/order-status-ui"
import { renderBusinessName } from "../../../utils/render-business-name"
import { renderName } from "../../../utils/render-name"
import classes from "./buyer-actions.module.css"

const ORDER_TONE: Record<string, Tone> = {
  REVIEWED: "amber",
  APPROVED: "green",
  AUTHORIZED: "green",
  DELIVERED: "green",
  CLOSED: "green",
  CANCELED: "red",
  REJECTED: "red",
}

/**
 * Canvas OrdB_Home: latest order card (lines, totals, reorder), the supplier card, and the
 * expiring-promotions block. Endpoints: POST /ord/order/list (latest), GET /ord/order/:id (lines;
 * list rows carry none), GET /ord/network/:supplierId (terms, discount, manager), GET
 * /inv/invoice/received?senderBusinessId (to pay, overdue: B53), delivered orders (last delivery)
 * and GET /pricing/buyer/promotions per supplier (B27) for the expiring-promotions block.
 */
const PROMO_SUPPLIERS = 5
const PROMO_ROWS = 4
export function BuyerLastOrder() {
  const { t } = useTranslation(["dashboard"])
  const navigate = useNavigate()
  const { reorderById } = useReorder()
  const INV_LIST = usePermission("INV_LIST")
  const [reordering, setReordering] = useState(false)

  const latest = useSWR(
    "dash.buyer.latest-order",
    () => OrderApi.list({ filter: { type: "", orderStatus: "", excel: "false" }, offset: { page: 1, limit: 1 } }),
    { revalidateOnFocus: false },
  )
  const last = latest.data?.rows?.[0]
  const full = useSWR(last?.id ? `dash.buyer.latest-order.${last.id}` : null, () => OrderApi.get(last!.id), {
    revalidateOnFocus: false,
  })
  const supplierId = last ? orderSupplierId(last) : ""
  const network = useSWR(supplierId ? `dash.buyer.supplier.${supplierId}` : null, () => NetworkApi.get(supplierId), {
    revalidateOnFocus: false,
  })

  const payable = useSWR(
    supplierId && INV_LIST.hasAccess ? `dash.buyer.supplier-payable.${supplierId}` : null,
    () => InvoiceApi.received({ filter: { status: "CONFIRMED", senderBusinessId: supplierId }, offset: { page: 1, limit: 1 } }),
    { revalidateOnFocus: false },
  )
  // The list is newest-created first; the last delivery is the latest deliveredDate among them.
  const delivered = useSWR(
    supplierId ? `dash.buyer.supplier-delivered.${supplierId}` : null,
    () =>
      OrderApi.list({
        filter: { type: "", orderStatus: "", deliveryNoteStatus: "DELIVERED", excel: "false" },
        offset: { page: 1, limit: 50 },
      }),
    { revalidateOnFocus: false },
  )
  const { suppliers } = useBuyerSuppliers(true)
  const promoSupplierIds: string[] = suppliers.slice(0, PROMO_SUPPLIERS).map((x: { id: string }) => x.id)
  const promos = useSWR(
    promoSupplierIds.length ? ["dash.buyer.promotions", ...promoSupplierIds] : null,
    async () => {
      const lists = await Promise.all(
        promoSupplierIds.map((id) =>
          PricingApi.buyerPromotions(id)
            .then((res) => (res?.rows || []).map((row) => ({ ...row, supplierId: id })))
            .catch(() => []),
        ),
      )
      return lists
        .flat()
        .sort((a, b) => (a.effectiveTo ? Date.parse(a.effectiveTo) : Infinity) - (b.effectiveTo ? Date.parse(b.effectiveTo) : Infinity))
    },
    { revalidateOnFocus: false },
  )

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const order: any = full.data ?? last
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const lines: any[] = Array.isArray(full.data?.lines) ? full.data.lines : []
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const net: any = network.data
  const status = String(last?.orderStatus ?? "").toUpperCase()
  const supplierName = last ? renderBusinessName(last.type === "SALES" ? last.senderBusiness : last.receiverBusiness) : ""
  const dash = "-"

  const onReorder = async () => {
    if (!last?.id) return
    setReordering(true)
    try {
      await reorderById(last.id)
    } finally {
      setReordering(false)
    }
  }

  // Canvas: «5% (1 хоногт 100% төлвөл 10%)» — the main term's discount, then the others in brackets.
  const options = termOptions(net)
  const withDiscount = options.filter((o) => o.discount?.discountValue)
  const mainDiscount = withDiscount.find((o) => o.id === net?.paymentTermId)
  const otherDiscounts = withDiscount
    .filter((o) => o !== mainDiscount)
    .map((o) => t("dashboard:buyerActions.discountOther", { term: o.description, value: discountValueText(o.discount!) }))
  const discountText =
    withDiscount.length === 0
      ? t("dashboard:buyerActions.none")
      : mainDiscount
        ? `${discountValueText(mainDiscount.discount!)}${otherDiscounts.length ? ` (${otherDiscounts.join("; ")})` : ""}`
        : otherDiscounts.join("; ")
  const managerName = net?.supplierStaff ? renderName(net.supplierStaff) : ""

  const summary = payable.data?.summary
  const payableText = !INV_LIST.hasAccess || !summary ? dash : currencyFormat(summary.amountToPay)
  const overdueText = !INV_LIST.hasAccess || !summary
    ? dash
    : summary.overdueCount > 0
      ? t("dashboard:buyerActions.overdueValue", { amount: currencyFormat(summary.overdueAmount), count: summary.overdueCount })
      : t("dashboard:buyerActions.none")
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const lastDeliveredAt = ((delivered.data?.rows || []) as any[])
    .filter((o) => orderSupplierId(o) === supplierId && o.deliveredDate)
    .map((o) => String(o.deliveredDate))
    .sort()
    .pop()
  const promoRows = (promos.data || []).slice(0, PROMO_ROWS)
  const supplierNameOf = (id: string) => renderBusinessName(suppliers.find((x: { id: string }) => x.id === id), "")

  return (
    <>
      <Box className={classes.lastGrid}>
        <Box className={classes.card}>
          <Box className={classes.cardHead}>
            <Box>
              <Text className={classes.cardTitle}>{t("dashboard:buyerActions.lastOrder")}<HelpIcon helpKey="order.dashboard.section.buyer-last-order" variant="section" /></Text>
              {last ? (
                <Text className={classes.sub}>
                  {[
                    last.salesCode,
                    last.purchaseCode,
                    dateFormat(last.createdAt),
                    supplierName,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </Text>
              ) : null}
            </Box>
            {last ? <DotPill tone={ORDER_TONE[status] ?? "grey"}>{orderStatusUi(status).label}</DotPill> : null}
          </Box>

          {last ? (
            <>
              <Box>
                {lines.map((line, i) => (
                  <Box key={line.id ?? i} className={classes.lineRow}>
                    <Avatar src={line.image || null} size={44} radius="md" />
                    <Box miw={0}>
                      <Text className={classes.lineName} lineClamp={1}>{line.name}</Text>
                      <Text className={classes.sub}>{line.skuCode}</Text>
                    </Box>
                    <Text className={`${classes.mono} ${classes.lineHideSm}`} fz="sm">
                      {`${Number(line.quantity) || 0} x ${currencyFormat(Number(line.price) || 0)}`}
                    </Text>
                    <Text className={classes.mono} fw={700} fz="sm">
                      {currencyFormat((Number(line.price) || 0) * (Number(line.quantity) || 0))}
                    </Text>
                  </Box>
                ))}
                {full.isLoading ? <Text className={classes.text}>{dash}</Text> : null}
              </Box>
              <Box className={classes.footer}>
                <Text className={classes.text}>
                  {t("dashboard:buyerActions.orderTotals", {
                    types: order?.itemTypeCount ?? lines.length,
                    qty: order?.totalItemCount ?? 0,
                    amount: currencyFormat(order?.totalAmount || 0),
                  })}
                </Text>
                <Box className={classes.actions} mt={0}>
                  <Button variant="default" onClick={() => navigate(`/buyer-sales/detail/${last.id}`)}>
                    {t("dashboard:buyerActions.details")}
                  </Button>
                  <Button color="dark" leftSection={<ArrowsClockwiseIcon size={16} />} loading={reordering} onClick={onReorder}>
                    {t("dashboard:buyerActions.reorder")}
                  </Button>
                </Box>
              </Box>
              <Text className={classes.sub}>{t("dashboard:buyerActions.reorderHint", { count: lines.length })}</Text>
            </>
          ) : (
            <Text className={classes.text}>{latest.isLoading ? dash : t("dashboard:buyerActions.lastOrderNone")}</Text>
          )}
        </Box>

        <Box className={classes.card}>
          <Text className={classes.cardTitle}>{t("dashboard:buyerActions.supplierTitle")}<HelpIcon helpKey="order.dashboard.section.buyer-supplier" variant="section" /></Text>
          {last ? (
            <>
              <Box className={classes.titleWrap}>
                <Box className={classes.iconBox}><BuildingsIcon size={18} /></Box>
                <Box miw={0}>
                  <Text className={classes.lineName} lineClamp={1}>{supplierName || dash}</Text>
                  <Text className={classes.sub}>
                    {`#${net?.refCode ?? dash} · ${t("dashboard:buyerActions.regNumber")} ${net?.regNumber ?? dash}`}
                  </Text>
                </Box>
              </Box>
              <Box>
                <InfoRow label={t("dashboard:buyerActions.paymentTerm")} value={net?.paymentTerm?.description || dash} wrap />
                <InfoRow label={t("dashboard:buyerActions.discount")} value={discountText} wrap />
                <InfoRow label={t("dashboard:buyerActions.payable")} value={payableText} />
                <InfoRow label={t("dashboard:buyerActions.overdue")} value={overdueText} />
                <InfoRow label={t("dashboard:buyerActions.lastDelivered")} value={lastDeliveredAt ? dateFormat(lastDeliveredAt) : dash} />
                <InfoRow label={t("dashboard:buyerActions.manager")} value={managerName || dash} />
              </Box>
              <Box className={classes.actions}>
                <Button variant="default" onClick={() => navigate("/buyer-customer")}>
                  {t("dashboard:buyerActions.changeSupplier")}
                </Button>
              </Box>
            </>
          ) : (
            <Text className={classes.text}>{dash}</Text>
          )}
        </Box>
      </Box>

      <Box className={classes.card} mb={20}>
        <Text className={classes.cardTitle}>{t("dashboard:buyerActions.promotions")}<HelpIcon helpKey="order.dashboard.section.buyer-promotions" variant="section" /></Text>
        {promoRows.length > 0 ? (
          <Box>
            {promoRows.map((p) => (
              <Box key={`${p.supplierId}-${p.id}`} className={classes.promoRow}>
                <Box className={classes.iconBox}><TagIcon size={18} /></Box>
                <Box miw={0}>
                  <Text className={classes.lineName} lineClamp={1}>{p.name}</Text>
                  <Text className={classes.sub} lineClamp={1}>
                    {[p.summary, supplierNameOf(p.supplierId)].filter(Boolean).join(" · ")}
                  </Text>
                </Box>
                <Text className={classes.sub}>
                  {p.effectiveTo ? t("dashboard:buyerActions.promoUntil", { date: dateFormat(p.effectiveTo) }) : dash}
                </Text>
                <Button variant="default" onClick={() => navigate(`/buyer-sales/promotions?supplier=${p.supplierId}`)}>
                  {t("dashboard:buyerActions.promoOpen")}
                </Button>
              </Box>
            ))}
          </Box>
        ) : (
          <Box className={classes.promoEmpty}>
            <Box className={classes.iconBox}><TagIcon size={18} /></Box>
            <Box>
              <Text className={classes.lineName}>{t("dashboard:buyerActions.promotionsEmptyTitle")}</Text>
              <Text className={classes.sub}>{t("dashboard:buyerActions.promotionsEmptyText")}</Text>
            </Box>
          </Box>
        )}
      </Box>
    </>
  )
}
